import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import MultiSelectDropDown from "components/DropDown/MultiselectDropDown";
import { getAdminReportingData } from "actions/reporting";
import ReactBsTable from "components/Tables/ReactBsTable";
import { getPublisherList } from "actions/publisher";
import { getProductTypeList } from "actions/productType";
import { getDealList } from "actions/deal";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import moment from "moment";
import DatePicker from "react-datepicker";
import { formatValues } from "components/Common/commonFunctions";
import { getProductTypeListByPublisherID } from "actions/productType";
import { exportCsv } from "actions/publisher";
import NotificationAlert from "react-notification-alert";
import "react-datepicker/dist/react-datepicker.css";
import { getDemandPartnerList } from "actions/reporting";

const columnConfig = [
  { key: "publisher", label: "Publisher" },
  { key: "date", label: "Date" },
  { key: "deal", label: "Deal" },
  { key: "productType", label: "Product" },
  {
    key: "rawRequests",
    label: "Raw Requests",
    formatter: (value) => value?.toLocaleString(),
  },
  {
    key: "rawImpressions",
    label: "Raw Impressions",
    formatter: (value) => value?.toLocaleString(),
  },
  {
    key: "revenue",
    label: "Revenue",
    formatter: (value) => <span className="font-weight-bold">{value}</span>,
  },
];

const sortFieldMap = {
  date: "Date",
  publisher:"CompanyName",
  deal:"DealName",
  demandPartner:"DemandPartner",
  productType:"ProductTypeName",
  rawRequests: "RawRequests",
  rawImpressions: "RawImpressions",
  revenue: "GrossRevenue",
  requests: "AdRequests",
  impressions: "Impressions",
  fillRate: "FillRate",
  grossRevenue: "GrossRevenue",
  erpm: "ERPM",
  netRevenue: "NetRevenue",
  Ecpm: "ECPM",
  publisherPayout: "PublisherPayout",
  pnl: "Pnl",
  pnlPercentage: "PnlPercentage",
  month: "Month",
  bcmFee: "BcmFeeAmount",
  channel: "Channel",
};

const allColumns = columnConfig.map((col) => ({
  text: col.label,
  dataField: col.key,
  formatter: col.formatter,
  sort: ![].includes(col.key),
  sortCaret: (order) => {
    if (!order) {
      return <span className="sort-caret"><i className="fa fa-sort" /></span>;
    }
    return order === "asc" ? (
      <span className="sort-caret"><i className="fa fa-sort-up" /></span>
    ) : (
      <span className="sort-caret"><i className="fa fa-sort-down" /></span>
    );
  },
}));

const formatterMap = {
  revenue: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  pnlPercentage: (value) => (
    <span
      className={value > 0 ? "text-success font-weight-bold" : "text-danger font-weight-bold"}
      style={{ display: "block", textAlign: "right" }}
    >
      {value}%
    </span>
  ),
  request: (value) => (
    <span
      className={value > 0 ? "text-success font-weight-bold" : "text-danger font-weight-bold"}
      style={{ display: "block", textAlign: "right" }}
    >
      {value}%
    </span>
  ),
  pnl: (value) => {
    const numValue = parseFloat(value) || 0;
    return (
      <span
        className={numValue > 0 ? "text-success font-weight-bold" : "text-danger font-weight-bold"}
        style={{ display: "block", textAlign: "right" }}
      >
        {numValue >= 0 ? `$${numValue.toFixed(2)}` : `-$${Math.abs(numValue).toFixed(2)}`}
      </span>
    );
  },
  netRevenue: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  publisherPayout: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  grossRevenue: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>${value}</span>
  ),
  Ecpm: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  fillRate: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}%</span>
  ),
  impressions: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  requests: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  rawImpressions: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  rawRequests: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  bcmFee: (value) => <span style={{ display: "block", textAlign: "right" }}>${value}</span>,
  erpm: (value) => <span style={{ display: "block", textAlign: "right" }}>{value}</span>,
  channel: (value) => <span style={{ display: "block", textAlign: "left" }}>{value}</span>,
};

const generateColumns = (viewType = "", viewType2, demandPartnerIDs = [], productTypeNames = []) => {
  // Define base metric columns (always present)
  const baseMetrics = [
    "rawRequests", "requests", "rawImpressions", "impressions",
    "fillRate", "grossRevenue", "erpm", "netRevenue", "Ecpm",
    "publisherPayout", "pnl", "pnlPercentage"
  ];


  // Show channel if:
  const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length !== 0) && 
                     (viewType === "PLACEMENT" || viewType === "" || !viewType);
  // Choose the first column(s) based on viewType
  let firstColumns = [];
  switch (viewType) {
    case "DATE":
      firstColumns = ["date"];
      break;
    case "PUBLISHER":
      firstColumns = ["publisher"];
      break;
    case "PRODUCT":
      firstColumns = ["productType"];
      break;
    case "DEALNAME":
      firstColumns = ["deal"];
      break;
    case "MONTH":
      firstColumns = ["month"];
      break;
    case "PLACEMENT":
      firstColumns = ["channel"];
      break;
    default:
      firstColumns = ["date", "publisher", "deal", "productType"];
      break;
  }
  
  if (viewType2 === "MONTH"){
    firstColumns = ["month"];
  }

  // If viewType is PLACEMENT, only show channel column and metrics, hide all other context columns
  if (viewType === "PLACEMENT") {
    firstColumns = ["channel"];
  }

  // Add channel after deal if:
  // - META is the only demand partner selected, OR
  // - Any META product type is selected
  // - And viewType is not PLACEMENT
  if (showChannel && viewType !== "PLACEMENT") {
    const dealIndex = firstColumns.indexOf("deal");
    if (dealIndex !== -1) {
      firstColumns = [
        ...firstColumns.slice(0, dealIndex + 1),
        "channel",
        ...firstColumns.slice(dealIndex + 1),
      ];
    } else {
      const insertIndex = firstColumns.indexOf("date") + 1 || 1;
      firstColumns = [
        ...firstColumns.slice(0, insertIndex),
        "channel",
        ...firstColumns.slice(insertIndex),
      ];
    }
  }

  // Combine first columns + base metrics
  const columnKeys = [...firstColumns, ...baseMetrics];

  const sortCaret = (order) => {
    if (!order) {
      return <span className="sort-caret" style={{ marginLeft: "8px" }}><i className="fa fa-sort" /></span>;
    }
    return order === "asc" ? (
      <span className="sort-caret" style={{ marginLeft: "8px" }}><i className="fa fa-sort-up" /></span>
    ) : (
      <span className="sort-caret" style={{ marginLeft: "8px" }}><i className="fa fa-sort-down" /></span>
    );
  };

  return columnKeys.map((key) => {
    const nonSortable = [];
    const isSortable = key === "channel" ? true : !nonSortable.includes(key);

    const contextColumns = ["date", "publisher", "deal", "productType", "month", "channel"];
    const isContext = contextColumns.includes(key);

    return {
      text: key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
      dataField: key,
      formatter: formatterMap[key] || ((value) => (
        <span style={{ display: "block", textAlign: isContext ? "left" : "right" }}>{value ?? "-"}</span>
      )),
      sort: isSortable,
      sortCaret,
      headerStyle: {
        textAlign:
          key === "rawRequests" ||
          key === "rawImpressions" ||
          key === "revenue" ||
          key === "pnl" ||
          key === "requests" ||
          key === "impressions" ||
          key === "fillRate" ||
          key === "grossRevenue" ||
          key === "netRevenue" ||
          key === "Ecpm" ||
          key === "publisherPayout" ||
          key === "erpm" ||
          key === "pnlPercentage"
            ? "right"
            : "left",
        padding: "8px 12px",
        margin: "4px 0",
      },
    };
  });
};

const Admin_Reporting = () => {
  const [data, setData] = useState({
    data: [],
    columns: allColumns,
    totalRecords: 0,
    totalCounts: null,
  });
  const [columnsModal, setColumnsModal] = useState(false);
  const [showLoader, setshowLoader] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setpageSize] = useState(50);
  const visibleColumnsSnapshot = useRef({});
  const notificationAlertRef = useRef(null);
  const isMounted = useRef(true);
  const initialLoadDone = useRef(false);

  const [filters, setFilters] = useState({
    report_type: "Processed",
    deal_search: [],
    product_type: [],
    view_by_options: "",
    publisher: [],
    date_range: "",
    start_date: "",
    end_date: "",
    view_type: "",
    filterParams: [],
    demand_partner: [], // Demand partner filter
  });
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });

  const [dropDownData, setDropDownData] = useState({
    publisherData: [],
    productTypeData: [],
    dealData: [],
    viewType: [
      { label: "Date", value: "DATE" }, 
      { label: "Publisher", value: "PUBLISHER" }, 
      { label: "Product", value: "PRODUCT" }, 
      { label: "Deal", value: "DEALNAME" },
      { label: "Month", value: "MONTH" },
      { label: "Channel", value: "PLACEMENT" }
    ],
    demandPartnerData: [],
  });

  const [visibleColumns, setVisibleColumns] = useState({});

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchPublishers();
    getProductTypeData();
    getDealData();
    getDemandPartners();
  }, []);

  // Fetch product types when publisher changes
  useEffect(() => {
    getProductTypeData(filters.publisher);
  }, [filters.publisher]);

  // Build and apply filter params when filters change
  useEffect(() => {
    const filterParams = [];

    if (Array.isArray(filters?.publisher) && filters.publisher.length > 0) {
      filterParams.push({
        field: "PublisherID",
        condition: "in",
        filterValues: filters.publisher.map(String),
      });
    }

    if (Array.isArray(filters?.deal_search) && filters.deal_search.length > 0) {
      filterParams.push({
        field: "DealID",
        condition: "in",
        filterValues: filters.deal_search.map(String),
      });
    }

    if (Array.isArray(filters?.product_type) && filters.product_type.length > 0) {
      filterParams.push({
        field: "ProductTypeID",
        condition: "in",
        filterValues: filters.product_type.map(String),
      });
    }

    if (Array.isArray(filters?.demand_partner) && filters.demand_partner.length > 0) {
      filterParams.push({
        field: "DemandPartnerID",
        condition: "in",
        filterValues: filters.demand_partner.map(String),
      });
    }

    if (filters?.start_date && filters?.end_date) {
      filterParams.push({
        field: "Date",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    const newFilterParams = filterParams.length > 0 ? JSON.stringify(filterParams) : "";

    setFilters((prev) => ({
      ...prev,
      filterParams: newFilterParams,
    }));
  }, [filters.publisher, filters.deal_search, filters.product_type, filters.start_date, filters.end_date, filters.demand_partner]);

  // SINGLE useEffect for fetching reporting data
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
    }

    const timer = setTimeout(() => {
      if (isMounted.current) {
        getAdminReportingList();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    pageNum,
    filters.filterParams,
    filters.report_type,
    pageSize,
    filters.view_type,
    filters.demand_partner,
    sortConfig.sortField,
    sortConfig.sortOrder,
  ]);

  // Update columns visibility when data changes
  useEffect(() => {
    if (!data?.columns?.length) return;

    const visibilityMap = data.columns.reduce((acc, col) => {
      acc[col.dataField] = true;
      return acc;
    }, {});

    setVisibleColumns(visibilityMap);
  }, [data.columns]);

  const toggleColumnsModal = () => {
    if (!columnsModal) {
      visibleColumnsSnapshot.current = { ...visibleColumns };
    }
    setColumnsModal(!columnsModal);
  };

  const handleCloseModal = () => {
    setVisibleColumns(visibleColumnsSnapshot.current);
    setColumnsModal(false);
  };

  const handleColumnToggle = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSizePerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setpageSize(newSize);
    setPageNum(1);
  };

  // Update tableColumns to filter based on view type first, then visible columns
  const tableColumns = useMemo(() => {
    if (!data.columns.length) return [];
    
    const getViewTypeColumns = () => {
      switch(filters.view_type) {
        case "DATE":
          return data.columns.filter(col => 
            col.dataField === "date" || 
            !["publisher", "deal", "productType"].includes(col.dataField)
          );
        case "PUBLISHER":
          return data.columns.filter(col => 
            col.dataField === "publisher" || 
            !["date", "deal", "productType"].includes(col.dataField)
          );
        case "PRODUCT":
          return data.columns.filter(col => 
            col.dataField === "productType" || 
            !["date", "publisher", "deal"].includes(col.dataField)
          );
        case "DEALNAME":
          return data.columns.filter(col => 
            col.dataField === "deal" || 
            !["date", "publisher", "productType"].includes(col.dataField)
          );
        case "MONTH":
          return data.columns.filter(col => 
            col.dataField === "month" || 
            !["date", "publisher", "deal", "productType"].includes(col.dataField)
          );
        case "PLACEMENT":
          // For placement, only show channel column and hide all other context columns
          return data.columns.filter(col => 
            col.dataField === "channel" || 
            !["date", "publisher", "deal", "productType", "month"].includes(col.dataField)
          );
        default:
          return data.columns;
      }
    };

    const viewTypeFilteredColumns = getViewTypeColumns();
    return viewTypeFilteredColumns.filter((col) => visibleColumns[col.dataField]);
  }, [visibleColumns, data, filters.view_type]);

  const isAnyFilterApplied = useCallback((filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return value && value.length > 0;
      }
      if (key === "report_type") return false;
      return value !== "" && value !== null && value !== undefined;
    });
  }, []);

  const handleChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return { ...prev, start_date: startDate };
        }

        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      let newFilters = { ...prev, [key]: value };

      if (key === "publisher") {
        newFilters.deal_search = [];
        newFilters.product_type = [];
      } else if (key === "product_type") {
        newFilters.deal_search = [];
      }
      return newFilters;
    });

    if (key === "publisher") {
      getProductTypeData(value);
      getDealData([], value);
    }

    if (key === "product_type") {
      getDealData(value, filters.publisher);
    }
    setPageNum(1);
  };

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) return;

    setFilters({
      report_type: filters.report_type,
      deal_search: [],
      product_type: [],
      view_by_options: "",
      publisher: [],
      date_range: "",
      start_date: "",
      end_date: "",
      filterParams: [],
      view_type: "",
      demand_partner: [],
    });

    setSortConfig({ dataField: "", sortField: "", sortOrder: "" });
    setPageNum(1);
    getProductTypeData();
    getDealData();
  };

  const getDemandPartners = async () => {
    try {
      const res = await getDemandPartnerList();
      if (!isMounted.current) return;

      const demandPartnerData = res?.code === 200
        ? (res?.data?.demandPartner || []).map((item) => ({
            label: item?.name || "",
            value: item?.ID,
          }))
        : [];

      setDropDownData((prev) => ({
        ...prev,
        demandPartnerData: demandPartnerData,
      }));
    } catch (error) {
      console.error("Error fetching demand partners:", error);
    }
  };

  const getProductTypeData = async (publisherIDs) => {
    try {
      let res;
      if (Array.isArray(publisherIDs) && publisherIDs.length > 0) {
        res = await getProductTypeListByPublisherID(publisherIDs);
      } else {
        res = await getProductTypeList(
          0,
          `[{"field":"Status","condition":"eq","filterValues":["ACTIVE"]}]`,
          ""
        );
      }

      if (!isMounted.current) return;

      let productTypeData = [];
      if (res?.code === 200) {
        const products = res?.data?.ProductType || res || [];
        productTypeData = products
          .filter((item) => item?.status === "ACTIVE")
          .map((item) => ({
            label: item?.name || "",
            value: item?.ID,
          }));
      }

      setDropDownData((prev) => ({
        ...prev,
        productTypeData: productTypeData,
      }));
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const getDealData = async (productTypeIDs, publisherIDs) => {
    try {
      const filters = [];
      if (Array.isArray(productTypeIDs) && productTypeIDs.length > 0) {
        filters.push({
          field: "ProductTypeID",
          condition: "in",
          filterValues: productTypeIDs.map(String),
        });
      }
      if (Array.isArray(publisherIDs) && publisherIDs.length > 0) {
        filters.push({
          field: "PublisherID",
          condition: "in",
          filterValues: publisherIDs.map(String),
        });
      }

      const res = await getDealList(
        0,
        filters.length > 0 ? JSON.stringify(filters) : "",
        ""
      );

      if (!isMounted.current) return;

      const dealData = res?.code === 200
        ? (res?.data?.Deal || []).map((item) => ({
          label: item?.name || "",
          value: item?.ID,
        }))
        : [];

      setDropDownData((prev) => ({
        ...prev,
        dealData: dealData,
      }));
    } catch (error) {
      console.error("Error fetching deals:", error);
    }
  };

  const fetchPublishers = async () => {
    try {
      const publisherRes = await getPublisherList(0);

      if (!isMounted.current) return;

      const publisherData = publisherRes?.code === 200
        ? (publisherRes?.data?.Publisher || []).map((item) => ({
          label: item?.companyName || "",
          value: item?.ID,
        }))
        : [];

      setDropDownData((prev) => ({
        ...prev,
        publisherData: publisherData,
      }));
    } catch (error) {
      console.error("Error fetching publishers:", error);
    }
  };

  const formatReportingRaw = (data = []) => {
    // Get demand partner IDs from filters to check if META is selected
    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    // Get product type names from the data
    const productTypeNames = data.map(item => item.productTypeID?.name || "").filter(Boolean);
    
    // Show channel if:
    // 1. META (3) is the ONLY demand partner selected, OR
    // 2. viewType is PLACEMENT, OR
   
  const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);
    return data.map((item) => {
      const formattedItem = {
        rawRequests: formatValues(item.totalRequest) || 0,
        rawImpressions: formatValues(item.totalImpressions) || 0,
        rpm: formatValues((item.grossRevenue / item.totalImpressions) * 1000) || 0,
        fillRate: formatValues((item.totalImpressions / item.totalRequest) * 100) || 0,
        grossRevenue: formatValues(item.grossRevenue) || 0,
        bcmFee: formatValues(item.grossRevenue - item.grossRevenue * 0.9) || 0,
        netRevenue: formatValues(item.grossRevenue * 0.9) || 0,
      };

      // Only add context fields if viewType is not PLACEMENT
      if (filters.view_type !== "PLACEMENT") {
        formattedItem.date = item.date || "-";
        formattedItem.publisher = item.publisher?.companyName || "-";
        formattedItem.deal = item.deal?.dealName || "-";
        formattedItem.adUnitCode = Array.isArray(item.deal?.adUnitCodes)
          ? item.deal.adUnitCodes.join(", ")
          : item.deal?.adUnitCodes || item.deal?.gamNetworkID || item.deal?.adUnitCode || "-";
        formattedItem.productType = item.productTypeID?.name || "-";
      }

      // Add channel column if showing channel
      if (showChannel) {
        formattedItem.channel = item.channel || "-";
      }
      
      return formattedItem;
    });
  };

  function formatMonth(monthStr) {
    if (!monthStr || monthStr === "-") return "-";
    
    const [year, month] = monthStr.split('-');
    const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  }

  const flattenProcessedData = (responseData = []) => {
    // Get demand partner IDs from filters to check if META is selected
    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    // Get product type names from the data
    const productTypeNames = responseData.map(item => item.productType?.name || "").filter(Boolean);
    
    // Show channel if:
    // 1. META (3) is the ONLY demand partner selected, OR
    // 2. viewType is PLACEMENT, OR
    // 3. Any META product type (iOS/Android) is selected
   
   const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);

    return responseData.map((item) => {
      const flattenedItem = {
        rawRequests: formatValues(item.rawRequests) || 0,
        requests: formatValues(item.adRequests) || 0,
        rawImpressions: formatValues(item.rawImpressions) || 0,
        impressions: formatValues(item.impressions) || 0,
        fillRate: formatValues(item.fillRate) || "0%",
        grossRevenue: formatValues(item.grossRevenue) || 0,
        erpm: formatValues(item.eRPM) || 0,
        netRevenue: formatValues(item.netRevenue) || 0,
        Ecpm: formatValues(item.eCPM) || 0,
        publisherPayout: formatValues(item.publisherPayout) || 0,
        pnl: item.pnl || 0,
        pnlPercentage: formatValues(item.pnlPercentage) || 0,
      };
      
      // Only add context fields if viewType is not PLACEMENT
      if (filters.view_type !== "PLACEMENT") {
        flattenedItem.month = formatMonth(item.month) || "-";
        flattenedItem.date = item.date || "-";
        flattenedItem.publisher = item.publisher?.companyName || "-";
        flattenedItem.deal = item.deal?.dealName || "-";
        flattenedItem.productType = item.productType?.name || "-";
      }
      
      // Add channel column if showing channel
      if (showChannel) {
        flattenedItem.channel = item.channel || "-";
      }
      
      return flattenedItem;
    });
  };

  const getAdminReportingList = async () => {
    try {
      setshowLoader(true);

      const res = await getAdminReportingData(
        pageNum,
        filters,
        "",
        pageSize,
        filters.view_type,
        sortConfig.sortField,
        sortConfig.sortOrder
      );

      if (!isMounted.current) return;

      if (res?.code === 200) {
        const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
        
        // Get product type names from the response data
        let productTypeNames = [];
        if (filters.report_type === "RAW") {
          productTypeNames = (res?.data?.ReportingRaw || []).map(item => item.productTypeID?.name || "").filter(Boolean);
        } else {
          productTypeNames = (res?.data?.reportingProcessed || []).map(item => item.productType?.name || "").filter(Boolean);
        }
        
        if (filters.report_type === "RAW") {
          const tableData = formatReportingRaw(res?.data?.ReportingRaw || []);
          const columns = generateColumns(filters.view_type, filters.report_type, demandPartnerIDs, productTypeNames);

          setData({
            data: tableData,
            columns: res?.data?.ReportingRaw?.length > 0 ? columns : columnConfig,
            totalRecords: res?.data?.totalRecords || 0,
            totalCounts: res?.data?.totalCounts || null,
          });
        } else {
          const tableData = flattenProcessedData(res?.data?.reportingProcessed || []);
          const columns = generateColumns(filters.view_type, filters.report_type, demandPartnerIDs, productTypeNames);

          setData({
            data: tableData,
            columns: res?.data?.reportingProcessed?.length > 0 ? columns : columnConfig,
            totalRecords: res?.data?.totalRecords || 0,
            totalCounts: res?.data?.totalCounts || null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching reporting list:", error);
    } finally {
      if (isMounted.current) {
        setshowLoader(false);
      }
    }
  };

  const notify = (type, message) => {
    notificationAlertRef.current?.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const handleExportCsv = async () => {
    try {
      setshowLoader(true);

      const res = await exportCsv(pageNum, filters, "", pageSize, filters.view_type);

      if (res && isMounted.current) {
        const blob = new Blob(['\uFEFF' + res], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        notify("success", "CSV exported successfully");
      } else {
        notify("danger", "No data received for export");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      notify("danger", "Something went wrong");
    } finally {
      if (isMounted.current) {
        setshowLoader(false);
      }
    }
  };

  const tableData = useMemo(() => {
    if (!data?.data?.length) return [];
  
    if (!data?.totalCounts) return data.data;

    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    
 const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);
  
    const totalRow = {
      srNo: "TOTAL_ROW",
      rawRequests: formatValues(data.totalCounts.totalOfRawRequests) || 0,
      requests: formatValues(data.totalCounts.totalOfRequests) || 0,
      impressions: formatValues(data.totalCounts.totalOfImpressions) || 0,
      rawImpressions: formatValues(data.totalCounts.totalOfRawImpressions) || 0,
      publisherPayout: formatValues(data.totalCounts.totalOfPublisherPayout) || 0,
      pnl: data.totalCounts.totalOfPnl || 0,
      pnlPercentage: formatValues((data.totalCounts.totalOfPnl / data.totalCounts.totalOfNetRevenue) * 100) || 0,
      erpm: formatValues((data.totalCounts.totalOfNetRevenue * 1000) / (data.totalCounts.totalOfImpressions)) || 0,
      Ecpm: formatValues((data.totalCounts.totalOfPublisherPayout * 1000) / data.totalCounts.totalOfImpressions) || 0,
      fillRate: formatValues(data.totalCounts.totalOfImpressions > 0 ? (data.totalCounts.totalOfImpressions / data.totalCounts.totalOfRequests) * 100 : 0),
      grossRevenue: formatValues(data.totalCounts.totalOfGrossRevenue) || 0,
      netRevenue: formatValues(data.totalCounts.totalOfNetRevenue) || 0,
    };

    // Only add context fields to total row if viewType is not PLACEMENT
    if (filters.view_type !== "PLACEMENT") {
      totalRow.publisher = "TOTAL";
      totalRow.deal = filters.view_type === "DEALNAME" ? "TOTAL" : "";
      totalRow.productType = filters.view_type === "PRODUCT" ? "TOTAL" : "";
      totalRow.date = filters.view_type === "DATE" ? "TOTAL" : "";
      totalRow.month = filters.view_type === "MONTH" ? "TOTAL" : "";
    }

    // Add channel to total row if showing channel and viewType is PLACEMENT
    if (showChannel && filters.view_type === "PLACEMENT") {
      totalRow.channel = "TOTAL";
    }

    return [...data.data, totalRow];
  }, [data, filters.view_type, filters.demand_partner]);
  
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
  };

  const filterSection = () => {
    return (
      <>
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        <Row className="align-items-center mb-2">
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.publisherData}
              placeholder="Select Publisher"
              value={filters.publisher}
              onChange={(e) => handleChange("publisher", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="publisher"
            />
          </Col>
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.productTypeData}
              placeholder="Product Type"
              value={filters.product_type}
              onChange={(e) => handleChange("product_type", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="product_type"
            />
          </Col>
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.dealData}
              placeholder="Select Deal"
              value={filters.deal_search}
              onChange={(e) => handleChange("deal_search", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="deal_search"
            />
          </Col>
            <Col md={2}>
              <SearchableDropdown
                options={dropDownData.viewType}
                placeholder="Select View Type"
                value={filters.view_type}
                onChange={(e) => handleChange("view_type", e.target.value)}
                isSearchable={true}
              />
            </Col>
        </Row>
        <Row className="align-items-center">
          <Col md={2}>
            <SearchableDropdown
              options={DATE_RANGE_OPTIONS}
              placeholder="Date Range"
              value={filters.date_range}
              onChange={(e) => showCustomDateInputs("date_range", e.target.value)}
              isSearchable={false}
            />
          </Col>
          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={filters.start_date ? new Date(filters.start_date) : null}
                onChange={(date) => handleChange("start_date", date ? formatDate(date) : "")}
                dateFormat="dd MMM yyyy"
                placeholderText="Start Date"
                className="form-control custom-datepicker"
                disabled={filters.date_range !== "Custom Range"}
              />
            </div>
          </Col>
          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={filters.end_date ? new Date(filters.end_date) : null}
                onChange={(date) => handleChange("end_date", date ? formatDate(date) : "")}
                dateFormat="dd MMM yyyy"
                placeholderText="End Date"
                className="form-control custom-datepicker"
                disabled={filters.date_range !== "Custom Range" || !filters.start_date}
                minDate={filters.start_date ? new Date(filters.start_date) : undefined}
              />
            </div>
          </Col>
          {filters.report_type !== "RAW" && (
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.demandPartnerData}
              placeholder="Select Demand Partner"
              value={filters.demand_partner}
              onChange={(e) => handleChange("demand_partner", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="demand_partner"
            />
          </Col>
          )}
          <Col md={2}>
            <Button
              className="secondary"
              onClick={handleReset}
              disabled={!isAnyFilterApplied(filters)}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Container fluid className="mt--6">
      <Card>
        {tableColumns?.length > 0 && (
          <ReactBsTable
            title="Admin Performance Report"
            data={tableData}
            columns={tableColumns}
            keyField="srNo"
            showAddbtn={false}
            handleSizePerPageChange={handleSizePerPageChange}
            renderFilters={filterSection}
            showColumnsBtn={true}
            Columnstext="Columns"
            toggleColumnsModal={toggleColumnsModal}
            exportCSVBtnShow={filters.report_type === "Processed"}
            ExportCSVtext="Export CSV"
            onexportCsvBtnClick={handleExportCsv}
            page={pageNum}
            enablePageSizeManagement={true}
            sizePerPage={pageSize}
            totalRecords={data?.totalRecords}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        )}
      </Card>

      <Modal isOpen={columnsModal} toggle={toggleColumnsModal}>
        <ModalHeader toggle={toggleColumnsModal}>Select Columns</ModalHeader>
        <ModalBody>
          {data?.columns.map((col) => (
            <label
              key={col.dataField}
              className="custom-checkbox-icon d-flex align-items-center mb-2"
            >
              <input
                type="checkbox"
                checked={visibleColumns[col.dataField]}
                onChange={() => handleColumnToggle(col.dataField)}
                hidden
              />
              <span className="checkbox-icon mr-2">
                {visibleColumns[col.dataField] && <i className="fa fa-check" />}
              </span>
              {col.text}
            </label>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button className="btn-gradient-primary" onClick={toggleColumnsModal}>
            Save Selection
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Admin_Reporting;